import { fetchWithDefaults } from '@/lib/http/client';
import type {
  AccessLevel,
  SharedContent,
  SharedConversation,
  ShareLink,
  ShareSettings,
} from '@/types/share';

class ShareService {
  /**
   * Get current share settings for an object
   * Returns null if no share link exists (404)
   */
  async getShareSettings(
    orgId: number,
    objectType: string,
    objectId: number | string
  ): Promise<ShareSettings | null> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/shares/${objectType}/${objectId}/`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Create or update a share link for an object
   */
  async createOrUpdateShare(
    orgId: number,
    objectType: string,
    objectId: number | string,
    accessLevel: AccessLevel
  ): Promise<ShareLink> {
    const response = await fetchWithDefaults(`/api/orgs/${orgId}/shares/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        object_type: objectType,
        object_id: objectId,
        access_level: accessLevel,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Delete a share link for an object
   * Handles 404 gracefully (already deleted)
   */
  async deleteShare(
    orgId: number,
    objectType: string,
    objectId: number | string
  ): Promise<void> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/shares/${objectType}/${objectId}/`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok && response.status !== 404) {
      throw new Error(`HTTP ${response.status}`);
    }
  }

  /**
   * Resolve a share link token to get the shared content
   * Returns SharedContent union type - caller should type-narrow based on object_type
   */
  async resolveShareLink(token: string): Promise<SharedContent> {
    const response = await fetchWithDefaults(`/s/${token}`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  }
}

export const shareService = new ShareService();

// Export types for use in components
export type {
  AccessLevel,
  SharedContent,
  SharedConversation,
  ShareLink,
  ShareSettings,
};
